import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { UserPlus } from 'lucide-react';

const USER_ROLES = [
  'Super Admin',
  'Project Head',
  'Key Account Manager',
  'Site Supervisor',
  'Billing Manager',
  'Billing Executive',
  'Account Manager',
  'Delivery Manager'
];

export default function RegisterPage() {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    roles: []
  });
  const [loading, setLoading] = useState(false);

  const handleRoleToggle = (role) => {
    setFormData(prev => ({
      ...prev,
      roles: prev.roles.includes(role)
        ? prev.roles.filter(r => r !== role)
        : [...prev.roles, role]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.roles.length === 0) {
      toast.error('Please select at least one role');
      return;
    }

    setLoading(true);

    const result = await register(formData);
    
    if (result.success) {
      toast.success('Registration successful! Please login.');
      navigate('/login');
    } else {
      toast.error(result.error);
    }
    
    setLoading(false);
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-gray-50" data-testid="register-page">
      {/* Left side - Form */}
      <div className="flex flex-col items-center justify-center p-8 lg:p-12 bg-white">
        <div className="w-full max-w-md space-y-8">
          <div className="space-y-3 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-[#DC143C] to-[#b91130] rounded-2xl mb-4 shadow-lg">
              <UserPlus className="h-8 w-8 text-white" />
            </div>
            <h1 className="text-5xl font-bold tracking-tight text-[#DC143C]" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Create Account
            </h1>
            <p className="text-gray-600 text-base">
              Register for Radiants Civiletric Portal
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="register-form">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-gray-700 font-semibold">Full Name</Label>
              <Input
                id="name"
                type="text"
                placeholder="John Doe"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                data-testid="name-input"
                className="h-12 border-2 focus:border-[#DC143C] focus:ring-[#DC143C]"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-gray-700 font-semibold">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@company.com"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                data-testid="email-input"
                className="h-12 border-2 focus:border-[#DC143C] focus:ring-[#DC143C]"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-gray-700 font-semibold">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="********"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                minLength={6}
                data-testid="password-input"
                className="h-12 border-2 focus:border-[#DC143C] focus:ring-[#DC143C]"
              />
            </div>

            <div className="space-y-3">
              <Label className="text-gray-700 font-semibold">Select Roles (one or multiple)</Label>
              <div className="grid grid-cols-2 gap-2 border-2 rounded-lg p-4 bg-gray-50">
                {USER_ROLES.map((role) => (
                  <div key={role} className="flex items-center space-x-2">
                    <Checkbox
                      id={`role-${role}`}
                      checked={formData.roles.includes(role)}
                      onCheckedChange={() => handleRoleToggle(role)}
                      data-testid={`role-checkbox-${role}`}
                      className="border-[#DC143C] data-[state=checked]:bg-[#DC143C] data-[state=checked]:border-[#DC143C]"
                    />
                    <Label
                      htmlFor={`role-${role}`}
                      className="text-sm font-medium cursor-pointer text-gray-700"
                    >
                      {role}
                    </Label>
                  </div>
                ))}
              </div>
            </div>

            <Button
              type="submit"
              className="w-full h-12 bg-gradient-to-r from-[#DC143C] to-[#b91130] hover:from-[#b91130] hover:to-[#8b0e25] text-white text-base font-semibold shadow-lg hover:shadow-xl transition-all duration-200"
              disabled={loading}
              data-testid="register-submit-button"
            >
              {loading ? (
                <span>Creating account...</span>
              ) : (
                <>
                  <UserPlus className="mr-2 h-5 w-5" />
                  Create Account
                </>
              )}
            </Button>
          </form>

          <div className="text-center text-sm">
            <span className="text-gray-600">Already have an account? </span>
            <Link to="/login" className="text-[#DC143C] hover:text-[#b91130] font-semibold hover:underline">
              Sign in
            </Link>
          </div>
        </div>
      </div>

      {/* Right side - Image */}
      <div
        className="hidden lg:block relative"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1750364567724-b3c7cf0642a7?crop=entropy&cs=srgb&fm=jpg&q=85)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div
          className="absolute inset-0"
          style={{
            background: 'linear-gradient(135deg, rgba(27, 39, 61, 0.95), rgba(220, 20, 60, 0.85))'
          }}
        >
          <div className="flex items-center justify-center h-full p-12">
            <div className="text-white space-y-6 max-w-lg">
              <h2 className="text-5xl font-bold leading-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Radiants Civiletric
              </h2>
              <p className="text-2xl text-gray-100 font-medium italic">
                Let The Spark Begins
              </p>
              <p className="text-lg text-gray-200 leading-relaxed">
                Join our platform for powerful workflow management. Building great things with value & quality.
              </p>
              <div className="flex gap-4 pt-4">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-white rounded-full"></div>
                  <span className="text-sm text-gray-100">Civil Services</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-white rounded-full"></div>
                  <span className="text-sm text-gray-100">Electrical</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-white rounded-full"></div>
                  <span className="text-sm text-gray-100">Fire Fighting</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
